/**
 * Case Note Service - client notes written in GO and the notes Mon Ami lists
 *
 * A note written here is a local row keyed by its offline_uuid until it
 * uploads; Mon Ami adopts a retried POST by that uuid, so a lost response
 * never creates the note twice. Uploaded notes are read-only in GO. A note
 * Mon Ami refuses is never deleted: it keeps the reason and waits for an edit
 * or an explicit retry.
 * Data reading should be done via useLiveQuery from Dexie React hooks.
 */

import db from '@/db/db';
import { API_ENDPOINTS } from '../../config/env';
import { getCurrentPerson } from './user_service';
import { fetchWithAuth, AuthExpiredError } from '../../lib/fetch-utils';
import { fetchWithCache } from '../../lib/api-client';
import { fetchCollection } from '../../lib/paged-collection';
import { softSignOut } from '../../lib/auth';
import { OfflineError } from '@/lib/sync-errors';
import { isOnline } from '../../lib/connectivity';
import { trackEvent } from './analytics_service';
import { CaseNote, NoteOptions, ServerCaseNote, isPendingCaseNote } from '../types/case_note';
import { createLogger, LogLevel } from '../../lib/logger';

const logger = createLogger('CaseNoteService', LogLevel.DEBUG);

export const ACCESS_LOST_MESSAGE = 'You no longer have access to this client.';

// ============================================================================
// Drafts
// ============================================================================

export type CaseNoteDraftInput = Omit<CaseNote,
  'id' | 'offline_uuid' | 'pinned' | 'created_at' | 'updated_at' | 'synced' | 'uploaded_at' | 'sync_error_message' | 'sync_error_at'>;

export async function createCaseNoteDraft(input: CaseNoteDraftInput): Promise<CaseNote> {
  const offlineUuid = crypto.randomUUID();
  const now = new Date().toISOString();
  const note: CaseNote = {
    ...input,
    id: offlineUuid,
    offline_uuid: offlineUuid,
    pinned: false,
    created_at: now,
    updated_at: now,
    synced: false,
    sync_error_message: null,
    sync_error_at: null,
  };
  await db.caseNotes.add(note);
  trackEvent('case_note.created', {
    client_id: note.client_id,
    note_type: note.type,
    is_offline: !isOnline(),
  }).catch(() => {});
  return note;
}

/** Edits a note that has not uploaded yet. Clears a refusal so the sweep sends it again. */
export async function updateCaseNoteDraft(id: number | string, changes: Partial<CaseNoteDraftInput>): Promise<CaseNote> {
  const existing = await db.caseNotes.get(id);
  if (!existing) throw new Error('This note is no longer on this device.');
  if (existing.synced) throw new Error('This note is already in Mon Ami. Open the client in Mon Ami to change it.');

  const updated: CaseNote = {
    ...existing,
    ...changes,
    updated_at: new Date().toISOString(),
    sync_error_message: null,
    sync_error_at: null,
  };
  await db.caseNotes.put(updated);
  return updated;
}

/** Deletes a note that has not uploaded yet. Uploaded notes stay. */
export async function discardCaseNoteDraft(id: number | string): Promise<void> {
  const existing = await db.caseNotes.get(id);
  if (!existing) return;
  if (existing.synced) throw new Error('This note is already in Mon Ami and cannot be discarded here.');
  await db.caseNotes.delete(id);
}

// ============================================================================
// Upload
// ============================================================================

export function buildCaseNotePayload(note: CaseNote) {
  const common = {
    type: note.type,
    offline_uuid: note.offline_uuid,
    interaction_date: note.interaction_date,
    interaction_type: note.interaction_type,
    note_subject_id: note.note_subject?.id ?? null,
    subject: note.note_subject ? null : note.subject,
    detail: note.detail,
  };
  if (note.type === 'BasicNote') return { note: common };

  const units = Object.fromEntries(
    note.service_units
      .filter(unit => Number(unit.unit_count) > 0)
      .map(unit => [
        `service_definition_${unit.service_definition_id}`,
        { unit_count: unit.unit_count, service_funding_source_id: unit.funding_source?.id ?? null },
      ])
  );

  return {
    note: {
      ...common,
      program_id: note.program?.id ?? null,
      organization_site_id: note.site?.id ?? null,
      check_in: note.check_in,
      note_service_units_attributes: units,
    },
  };
}

interface RefusalBody {
  error?: string;
  message?: string;
  messages?: string[];
  errors?: Record<string, string[]> | string[];
}

function refusalMessage(status: number, body: RefusalBody): string {
  if (status === 404) return ACCESS_LOST_MESSAGE;
  if (Array.isArray(body.messages) && body.messages.length > 0) return body.messages.join(' ');
  if (body.message) return body.message;
  if (Array.isArray(body.errors) && body.errors.length > 0) return body.errors.join(' ');
  if (body.errors && typeof body.errors === 'object') {
    const parts = Object.entries(body.errors as Record<string, string[]>).flatMap(([field, messages]) =>
      (messages ?? []).map((message: string) => `${field.replace(/_/g, ' ')} ${message}`)
    );
    if (parts.length > 0) return parts.join('. ');
  }
  if (status === 403) return "You don't have permission to add this note.";
  return 'Mon Ami could not accept this note.';
}

/** Stores a server note for a client, replacing any local copy under its offline_uuid. */
function toLocalNote(record: ServerCaseNote, clientId: number, organizationId: number, extra: Partial<CaseNote> = {}): CaseNote {
  return {
    ...record,
    service_units: record.service_units ?? [],
    client_id: clientId,
    organization_id: organizationId,
    synced: true,
    sync_error_message: null,
    sync_error_at: null,
    ...extra,
  };
}

/**
 * Uploads one note. Resolves with the stored row: the server copy once it
 * uploads, or the local copy carrying the reason Mon Ami refused it. Throws on
 * connectivity problems (the note stays pending, with no reason) and on an
 * expired session.
 */
export async function syncCaseNote(note: CaseNote): Promise<CaseNote> {
  if (note.synced) return note;
  if (!isOnline()) throw new OfflineError('Cannot sync notes while offline');

  const currentPerson = await getCurrentPerson();
  if (!currentPerson) throw new Error('No current person selected - cannot sync note');

  const url = `${API_ENDPOINTS.clients}/${note.client_id}/notes?current_person_id=${currentPerson.id}`;
  const response = await fetchWithAuth(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(buildCaseNotePayload(note)),
    timeout: 20000,
  }, () => {
    // Soft cleanup that keeps the local DB: the note uploads after sign-in.
    softSignOut();
  });

  if (response.ok) {
    const record = await response.json() as ServerCaseNote;
    const uploaded = toLocalNote(record, note.client_id, note.organization_id, { uploaded_at: new Date().toISOString() });
    await db.transaction('rw', db.caseNotes, async () => {
      if (uploaded.id !== note.id) await db.caseNotes.delete(note.id);
      await db.caseNotes.put(uploaded);
    });
    trackEvent('case_note.uploaded', { client_id: note.client_id, note_id: uploaded.id, note_type: note.type }).catch(() => {});
    return uploaded;
  }

  if ([403, 404, 409, 422].includes(response.status)) {
    const body = await response.json().catch(() => ({})) as RefusalBody;
    const message = refusalMessage(response.status, body);
    const refused: CaseNote = { ...note, sync_error_message: message, sync_error_at: new Date().toISOString() };
    await db.caseNotes.put(refused);
    trackEvent('case_note.upload_failed', {
      client_id: note.client_id,
      note_type: note.type,
      http_status: response.status,
      error_message: message,
    }).catch(() => {});
    return refused;
  }

  throw new Error(`Note upload failed: ${response.status} ${response.statusText}`);
}

/** "Try again": sends a refused note once more. */
export async function retryCaseNote(id: number | string): Promise<CaseNote | null> {
  const note = await db.caseNotes.get(id);
  if (!note) return null;
  return syncCaseNote({ ...note, sync_error_message: null });
}

export interface CaseNoteSweepResult {
  /** Notes uploaded by this sweep. */
  synced: number;
  /** Notes this sweep attempted that are still on the device only. */
  failed: number;
}

// Coalesces overlapping sweeps, like the contact sweep: reconnect fans out to
// several triggers and parallel sweeps would read the same pending snapshot.
let sweepInFlight: Promise<CaseNoteSweepResult> | null = null;
let sweepRerunRequested = false;

/**
 * Upload every note waiting on this device. Notes Mon Ami already refused are
 * held: sending the same note again would be refused the same way. An edit or
 * "Try again" clears the refusal.
 */
export function syncOfflineCaseNotes(): Promise<CaseNoteSweepResult> {
  if (sweepInFlight) {
    sweepRerunRequested = true;
    return sweepInFlight;
  }
  sweepInFlight = runSweep().finally(() => {
    sweepInFlight = null;
  });
  return sweepInFlight;
}

async function runSweep(): Promise<CaseNoteSweepResult> {
  let synced = 0;
  const attemptedIds = new Set<number | string>();
  let failed = 0;
  do {
    sweepRerunRequested = false;
    const pending = await db.caseNotes.filter(note => isPendingCaseNote(note) && !note.sync_error_message).toArray();

    for (const note of pending) {
      attemptedIds.add(note.id);
      try {
        const result = await syncCaseNote(note);
        if (result.synced) synced++;
      } catch (error) {
        if (error instanceof AuthExpiredError || error instanceof OfflineError) throw error;
        logger.error(`Failed to sync note ${note.id}:`, error);
      }
    }

    // Notes that uploaded changed ids, so count what is still pending under the attempted ids.
    failed = attemptedIds.size === 0
      ? 0
      : await db.caseNotes.where('id').anyOf([...attemptedIds]).filter(isPendingCaseNote).count();
  } while (sweepRerunRequested);

  return { synced, failed };
}

/** Notes written on this device that have not uploaded, for one organization. */
export async function countPendingCaseNotes(organizationId: number): Promise<number> {
  return db.caseNotes.where('organization_id').equals(organizationId).filter(isPendingCaseNote).count();
}

export async function hasPendingCaseNotes(organizationId: number): Promise<boolean> {
  return (await db.caseNotes.where('organization_id').equals(organizationId).filter(isPendingCaseNote).first()) !== undefined;
}

export interface CaseNoteSyncError {
  noteId: number | string;
  clientId: number;
  clientSlug: string;
  interactionDate: string;
  message: string;
  path: string;
}

/** Notes Mon Ami refused, for Settings › Sync. */
export async function listCaseNoteSyncErrors(organizationId: number): Promise<CaseNoteSyncError[]> {
  const refused = await db.caseNotes
    .where('organization_id')
    .equals(organizationId)
    .filter(note => isPendingCaseNote(note) && Boolean(note.sync_error_message))
    .toArray();

  return Promise.all(refused.map(async note => {
    const client = await db.clients.get(note.client_id);
    const slug = client?.label ?? String(note.client_id);
    return {
      noteId: note.id,
      clientId: note.client_id,
      clientSlug: slug,
      interactionDate: note.interaction_date,
      message: note.sync_error_message!,
      path: `/clients/${slug}/notes/${note.id}`,
    };
  }));
}

// ============================================================================
// Pull
// ============================================================================

function parseNotesPage(data: unknown): ServerCaseNote[] | null {
  if (Array.isArray(data)) return data as ServerCaseNote[];
  const records = (data as { records?: unknown } | null)?.records;
  return Array.isArray(records) ? records as ServerCaseNote[] : null;
}

/**
 * Pull a client's notes. Mon Ami lists the last 12 months plus pinned notes;
 * an uploaded note it no longer lists is removed after a complete walk. Notes
 * that have not uploaded are never removed here.
 */
export async function refreshClientNotes(clientId: number): Promise<void> {
  const currentPerson = await getCurrentPerson();
  if (!currentPerson) {
    logger.debug('No current person - skipping notes refresh');
    return;
  }
  const organizationId = currentPerson.organization.id;
  // A note uploaded while this walk runs is missing from the list it holds.
  const walkStartedAt = Date.now();

  const collection = await fetchCollection<ServerCaseNote>({
    organizationId,
    cacheKey: `notes_${clientId}`,
    url: page => `${API_ENDPOINTS.clients}/${clientId}/notes?current_person_id=${currentPerson.id}&page=${page}`,
    parse: parseNotesPage,
    localIds: async ids => new Set(await db.caseNotes.where('id').anyOf(ids).filter(n => n.client_id === clientId).primaryKeys()),
    timeout: 30000,
  });

  if (!collection.firstPageOk) {
    if (collection.status !== 404 && collection.status !== 304 && !collection.httpOk) {
      logger.warn(`Failed to fetch notes for client ${clientId}: ${collection.status}`);
    }
    return;
  }

  await db.transaction('rw', db.caseNotes, async () => {
    for (const record of collection.records) {
      // An upload whose response was lost: the server copy replaces the local one.
      if (record.offline_uuid) {
        const local = await db.caseNotes.get(record.offline_uuid);
        if (local && !local.synced) await db.caseNotes.delete(record.offline_uuid);
      }
      const existing = await db.caseNotes.get(record.id);
      await db.caseNotes.put(toLocalNote(record, clientId, organizationId, { uploaded_at: existing?.uploaded_at ?? null }));
    }

    if (!collection.complete) return;

    const local = await db.caseNotes.where('client_id').equals(clientId).toArray();
    for (const note of local) {
      if (typeof note.id !== 'number' || collection.ids.has(note.id) || isPendingCaseNote(note)) continue;
      if (note.uploaded_at && new Date(note.uploaded_at).getTime() >= walkStartedAt) continue;
      await db.caseNotes.delete(note.id);
    }
  });

  if (!collection.complete) {
    logger.warn(`The notes walk for client ${clientId} was incomplete; keeping local notes until the next refresh`);
  }
  await collection.commit();
}

const REFRESH_NOTES_CONCURRENCY = 4;

/** Pull notes for every client of the current organization, so they can be read offline. */
export async function refreshNotes(): Promise<void> {
  const currentPerson = await getCurrentPerson();
  if (!currentPerson) return;

  const clients = await db.clients.where('organization_id').equals(currentPerson.organization.id).toArray();
  for (let i = 0; i < clients.length; i += REFRESH_NOTES_CONCURRENCY) {
    const batch = clients.slice(i, i + REFRESH_NOTES_CONCURRENCY);
    await Promise.all(batch.map(async client => {
      try {
        await refreshClientNotes(client.id as number);
      } catch (error) {
        if (error instanceof AuthExpiredError) throw error;
        logger.warn(`Error fetching notes for client ${client.id}:`, error);
      }
    }));
  }
}

/** Pull the note form's options (programs, sites, coding, subjects, service units). */
export async function refreshNoteOptions(): Promise<void> {
  const currentPerson = await getCurrentPerson();
  if (!currentPerson) return;
  const organizationId = currentPerson.organization.id;

  const url = `${API_ENDPOINTS.noteOptions}?current_person_id=${currentPerson.id}`;
  const { response, notModified, commit } = await fetchWithCache(url, {
    organizationId,
    name: `note_options_person_${currentPerson.id}`,
  }, {
    timeout: 15000,
    haveBody: async () => (await db.noteOptions.get(organizationId)) !== undefined,
  });

  if (notModified) return;
  if (!response.ok) {
    // An older Mon Ami without the endpoint: nothing to offer, not an error.
    if (response.status === 404) return;
    throw new Error(`Failed to fetch note options: ${response.status}`);
  }

  const data = await response.json() as Omit<NoteOptions, 'organization_id'>;
  if (!data || !Array.isArray(data.programs) || !Array.isArray(data.note_types)) {
    logger.warn('Unexpected note options response format');
    return;
  }

  await db.noteOptions.put({ ...data, organization_id: organizationId });
  await commit();
}
